<script>
import FormItem from '@/components/FormItem'
import FormInputWrapper from '@/components/FormInputWrapper'
import FormLabel from '@/components/FormLabel'
import FormLabelMessage from '@/components/FormLabelMessage'
import Icon from '@/components/Icon'

export default {
  name: 'SelectField',

  components: {
    FormItem,
    FormInputWrapper,
    FormLabel,
    FormLabelMessage,
    Icon,
  },

  // Componente personalizado para options
  provide () {
    return {
      selectField: this,
    }
  },

  props: {
    placeholder: {
      type: String,
      default: null,
    },

    label: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    value: {
      type: [String, Number],
      default: null,
    },

    validation: {
      type: Object,
      default: null,
    },

    // Nova prop para estilo arredondado
    round: {
      type: Boolean,
      default: false,
    },

    // Nova prop para tamanho do arredondamento
    rounded: {
      type: [String, Number],
      default: 'medium', // small, medium, large ou valor numérico
    },

    outlined: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      isFocused: false,
      isHover: false,
      isDropdownOpen: false,
    }
  },

  computed: {
    hasError () {
      return (this.validation && this.validation.$error) || false
    },

    computedClasses () {
      return {
        [[this.$classes['form-select']]]: true,
        [[this.$classes['is-disabled']]]: this.disabled,
        [[this.$classes['is-round']]]: this.round,
        [[this.$classes[`rounded-${this.rounded}`]]]: this.round && this.rounded,
        [[this.$classes['is-outlined']]]: this.outlined,
      }
    },

    inputName () {
      return `input-${this._uid}`
    },

    getOptions () {
      if (!this.$slots.default) return []

      const options = this.$slots.default
        .filter(node => {
          // Filtra elementos option ou template que contenham options
          const isOption = node.tag === 'option'
          const isTemplate = node.tag === 'template'
          return isOption || isTemplate
        })
        .flatMap(node => {
          if (node.tag === 'option') {
            // Opção direta
            return [{
              value: node.data?.attrs?.value || node.data?.props?.value || node.data?.domProps?.value || '',
              text: node.children && node.children[0] ? node.children[0].text : '',
            }]
          } else if (node.tag === 'template') {
            // Template com opções - processa os children
            return (node.children || [])
              .filter(child => child.tag === 'option')
              .map(child => ({
                value: child.data?.attrs?.value || child.data?.props?.value || child.data?.domProps?.value || '',
                text: child.children && child.children[0] ? child.children[0].text : '',
              }))
          }
          return []
        })
        .filter(option => option.value !== undefined && option.value !== '' && option.text)

      return options
    },

    // Computed para obter o texto da opção selecionada
    getSelectedOptionText () {
      if (!this.getOptions || this.getOptions.length === 0) return ''

      const selectedOption = this.getOptions.find(option => option.value === this.value)
      return selectedOption ? selectedOption.text : ''
    },
  },

  methods: {
    handleOver () {
      this.isHover = true
    },

    handleLeave () {
      this.isHover = false
    },

    updateValue (value) {
      this.validation && this.validation.$touch()
      this.$emit('input', value)
    },

    updateFocus () {
      this.isFocused = true
    },

    updateBlur (event) {
      this.isFocused = false
      this.validation && this.validation.$touch()
    },

    toggleDropdown () {
      if (this.round && !this.disabled) {
        this.isDropdownOpen = !this.isDropdownOpen
      }
    },

    closeDropdown () {
      this.isDropdownOpen = false
    },

    // Método para selecionar uma opção no dropdown customizado
    selectOption (value) {
      this.updateValue(value)
      this.closeDropdown()
    },

    // Método para detectar cliques fora do dropdown
    handleClickOutside (event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown()
      }
    },

    // Tira o foco do select nativo (usado para evitar interação repetida após selecionar)
    blur () {
      if (this.$refs.nativeSelect) {
        this.$refs.nativeSelect.blur()
      }
    },
  },

  created () {
  },

  mounted () {
    if (this.round) {
      document.addEventListener('click', this.handleClickOutside)
    }
  },

  beforeDestroy () {
    if (this.round) {
      document.removeEventListener('click', this.handleClickOutside)
    }
  },
}
</script>

<template>
  <form-item>
    <form-label v-if="label" :text="label" :input-name="inputName" :error="hasError" :disabled="disabled"
      :validation="validation">
      <form-label-message :validation="validation" />
    </form-label>
    <form-input-wrapper :focus="isFocused" :error="hasError" :disabled="disabled" :round="round" :rounded="rounded"
      :outlined="outlined">
      <!-- Select padrao -->
      <select v-if="!round" ref="nativeSelect" :id="inputName" :placeholder="placeholder" :disabled="disabled"
        :readonly="readonly" :class="computedClasses" :value="value" @mouseover="handleOver" @mouseleave="handleLeave"
        @focus="updateFocus" @blur="updateBlur" @change="updateValue($event.target.value)">
        <option value="" selected v-if="placeholder">{{ placeholder }}</option>
        <slot />
      </select>

      <!-- estilo arredondado -->
      <div v-else :class="[$classes['custom-select-input'], computedClasses]" @click="toggleDropdown">
        <span :class="$classes['selected-value']">
          {{ getSelectedOptionText || placeholder || 'Selecione uma opção' }}
        </span>
        <icon name="caret-down" :class="{
          [$classes['select-icon']]: true,
          [$classes['is-hover']]: isHover,
          [$classes['is-disabled']]: disabled,
          [$classes['is-rotated']]: isDropdownOpen,
        }" />
      </div>

      <!-- Dropdown customizado para estilo arredondado -->
      <div v-if="round && isDropdownOpen" :class="$classes['custom-dropdown']">
        <div :class="$classes['dropdown-options']">
          <div v-if="placeholder" :class="$classes['dropdown-option']" @click="selectOption('')">
            {{ placeholder }}
          </div>
          <div v-for="(option, index) in getOptions" :key="index"
            :class="[$classes['dropdown-option'], { [$classes['is-selected']]: option.value === value }]"
            @click="selectOption(option.value)">
            <span :class="$classes['option-text']">{{ option.text }}</span>
          </div>
        </div>
      </div>

      <!-- Ícone para select não arredondado -->
      <icon v-if="!round" name="caret-down" :class="{
        [$classes['select-icon']]: true,
        [$classes['is-hover']]: isHover,
        [$classes['is-disabled']]: disabled,
      }" />
    </form-input-wrapper>
  </form-item>
</template>

<style module>
.form-select {
  cursor: pointer;
  display: block;
  width: 100%;
  height: 35px;
  flex: 1;
  appearance: none;
  border: 0;
  border-radius: 0;
  font-size: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, Cantarell, "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  color: #292929;
  background: none;
  transition: all 0.2s ease-in-out;
}

.form-select:disabled,
.form-select.is-round:disabled,
.form-select.is-outlined:disabled,
.custom-select-input:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
  position: relative;
}

.form-select.is-outlined {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  padding: 8px 12px;
  height: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-select.is-outlined:hover {
  border-color: #bdbdbd;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.form-select.is-outlined:focus {
  border-color: #4A94D4;
  box-shadow: 0 0 0 3px rgba(74, 148, 212, 0.1);
  outline: none;
}

.form-select.is-outlined:disabled {
  border-color: #dee2e6;
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.8;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08);
}

.form-select.is-round {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  padding: 8px 16px;
  gap: 5px;
  height: 40px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-select.is-round:hover {
  background: #e9ecef;
  border-color: #dee2e6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.form-select.is-round:focus {
  background: #ffffff;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  outline: none;
}

.form-select.is-round option {
  background: #ffffff;
  color: #495057;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  margin: 2px 0;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.form-select.is-round option:hover {
  background: #f8f9fa;
  color: #212529;
}

.form-select.is-round option:checked {
  background: #4A94D4;
  color: #ffffff;
  font-weight: 600;
}

.form-select.is-round option:selected {
  background: #4A94D4;
  color: #ffffff;
  font-weight: 600;
}

.form-select.is-round:focus option:checked {
  background: linear-gradient(0deg, #4A94D4 0%, #4A94D4 100%);
  color: #ffffff;
  font-weight: 600;
}

.form-select.is-round:focus {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.form-select.is-round optgroup {
  background: #f8f9fa;
  color: #6c757d;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  margin: 4px 0;
}

.custom-select-input {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 20px;
  padding: 8px 16px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  text-transform: none;
}

.custom-select-input:hover {
  background: #e9ecef;
  border-color: #dee2e6;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.custom-select-input:focus {
  background: #ffffff;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  outline: none;
}

.selected-value {
  flex: 1;
  text-align: left;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: dropdownSlideDown 0.2s ease-out;
}

@keyframes dropdownSlideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f8f9fa;
  position: relative;
  font-size: 14px;
  color: #495057;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background: #f8f9fa;
}

.dropdown-option.is-selected {
  background: #4A94D4;
  color: #ffffff;
  font-weight: 600;
}

.option-text {
  flex: 1;
  text-align: left;
}

.select-icon.is-rotated {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

.form-select.rounded-small {
  border-radius: 8px;
}

.form-select.rounded-medium {
  border-radius: 20px;
}

.form-select.rounded-large {
  border-radius: 30px;
}

.custom-select-input.rounded-small {
  border-radius: 8px;
}

.custom-select-input.rounded-medium {
  border-radius: 20px;
}

.custom-select-input.rounded-large {
  border-radius: 30px;
}

.select-icon {
  position: absolute;
  top: 5px;
  right: 0;
  pointer-events: none;
  color: #A2A2A2;
}

.select-icon.is-hover {
  color: #292929;
}

.select-icon.is-disabled {
  color: #adb5bd;
  opacity: 0.6;
  filter: grayscale(0.3);
}

.custom-select-input .select-icon {
  position: static !important;
  top: auto !important;
  right: auto !important;
  transform: none !important;
  color: #6c757d;
  margin-left: auto;
}

.custom-select-input:hover .select-icon {
  color: #4A94D4;
}

.custom-select-input .select-icon,
.custom-select-input .select-icon.is-hover,
.custom-select-input .select-icon.is-disabled {
  position: static !important;
  top: auto !important;
  right: auto !important;
  transform: none !important;
}

.custom-select-input:disabled .select-icon {
  color: #adb5bd;
  opacity: 0.6;
  filter: grayscale(0.3);
}
</style>
